import React, { useState, useEffect } from 'react';
import YonotsuneMenu from './yonotsune/YonotsuneMenu.js';
import YonotsuneAchievementScreen from './yonotsune/YonotsuneAchievementScreen.js';
import YonotsuneInitialDeckSelection from './yonotsune/YonotsuneInitialDeckSelection.js';
import YonotsuneRecordsModal from './yonotsune/YonotsuneRecordsModal.js';
import YonotsuneNodeMap from './yonotsune/YonotsuneNodeMap.js';
import YonotsuneEventResolver from './yonotsune/YonotsuneEventResolver.js';
import YonotsuneRewardScreen from './YonotsuneRewardScreen.js';
import YonotsuneHelpOverlay from './overlays/YonotsuneHelpOverlay.js';
import { BOSS_DECKS, INITIAL_DECKS } from '../hooks/useYonotsune.js';
import { getUILoc, groupCards, getCardName } from '../utils/cardUtils.js';
import { CardIllustration } from './Card.js';
import CardDetail from './CardDetail.js';
import audioManager from '../utils/AudioManager.js';
import { BGMCategory } from '../gameLogic/constants.js';
import './YonotsuneScreen.css';

const YonotsuneScreen = ({
  yonotsune,
  cardDefs,
  onStartMatch,
  onBackToTitle
}) => {
  useEffect(() => {
    audioManager.ensureCategoryBGM(BGMCategory.YONOTSUNE);
  }, []);
  const {
    activeRun,
    records,
    achievements,
    toast,
    startNewRun,
    selectNode,
    goToNextNodeSelection,
    resetRun,
    executeWhimExchange,
    executeCrystal,
    hesitateCrystal,
    executeSharpenRemove,
    executeDiplomacyExchange,
    requestDiplomacyReroll,
    executeOpportunityRemove,
    executeOpportunityDraw,
    startOpportunityBattle,
    exportAchievements,
    importAchievements
  } = yonotsune;

  const [currentView, setCurrentView] = useState('menu'); // 'menu', 'achievements', 'deck_selection', 'run'
  const [showHelp, setShowHelp] = useState(false);
  const [showRecords, setShowRecords] = useState(false);
  const [hasInitializedView, setHasInitializedView] = useState(false);
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);
  const [previewingDeck, setPreviewingDeck] = useState(false);
  const [previewingMemory, setPreviewingMemory] = useState(false);
  const [selectedDetailCard, setSelectedDetailCard] = useState(null);

  useEffect(() => {
    if (activeRun && !hasInitializedView) {
      setCurrentView('run');
      setHasInitializedView(true);
    } else if (!activeRun && hasInitializedView) {
      setCurrentView('menu');
      setHasInitializedView(false);
    }
  }, [activeRun, hasInitializedView]);

  // 音声再生ヘルパー
  const playSE = (seName) => {
    audioManager.playSE(seName);
  };

  const handleStartNewRun = () => {
    audioManager.playSE('select');
    setCurrentView('deck_selection');
  };

  const handleSelectDeck = (deckKey) => {
    audioManager.playSE('place');
    startNewRun(deckKey);
    setCurrentView('menu');
  };

  const handleShowAchievements = () => {
    audioManager.playSE('select');
    setCurrentView('achievements');
  };

  const handleCloseSubView = () => {
    audioManager.playSE('cancel');
    setCurrentView('menu');
  };

  const handleCloseBossReveal = () => {
    audioManager.playSE('select');
    yonotsune.dismissBossReveal();
  };

  const handleOpenDeckPreview = () => {
    audioManager.playSE('select');
    setPreviewingDeck(true);
  };
  
  const handleOpenMemoryPreview = () => {
    audioManager.playSE('select');
    setPreviewingMemory(true);
  };
  
  // ----------------------------------------------------
  // 共通HUDコンポーネント
  // ----------------------------------------------------
  const YonotsuneHUD = ({ run, onAbandon, onCheckDeck, onShowMemory, onSuspend }) => {
    const totalCon = 12 + run.consciousnessBonus;
    const totalScale = 5 + run.scaleBonus;
    const boss = BOSS_DECKS[run.bossKey];
    // bossKeyから翻訳名を取得、なければ保存されているbossDeckNameを表示（互換性）、それもなければkey自体を表示
    const localizedBossName = boss ? getUILoc(boss.nameKey) : (run.bossDeckName || run.bossKey || '???');

    return (
      <div className="run-hud" style={{ zIndex: 10 }}>
        <div className="hud-item">
          <span className="label">{getUILoc('YONOTSUNE.HUD.MATCHES')}:</span>
          <span className="value">{run.matchedCount} / 10</span>
        </div>
        <div className="hud-item">
          <span className="label">{getUILoc('YONOTSUNE.HUD.PHASE')}:</span>
          <span className="value">{run.currentPhase}</span>
        </div>
        <div className="hud-item stat-item">
          <span className="label">{getUILoc('YONOTSUNE.HUD.INIT_CON')}:</span>
          <span className="value text-heal">{totalCon} ({run.consciousnessBonus > 0 ? `+${run.consciousnessBonus}` : run.consciousnessBonus})</span>
        </div>
        <div className="hud-item stat-item">
          <span className="label">{getUILoc('YONOTSUNE.HUD.INIT_SCALE')}:</span>
          <span className="value text-scale">{totalScale} ({run.scaleBonus > 0 ? `+${run.scaleBonus}` : run.scaleBonus})</span>
        </div>
        <div className="hud-item">
          <button className="yonotsune-btn memory-btn" style={{ padding: '4px 10px', fontSize: '0.75rem', marginRight: '5px' }} onClick={(e) => { e.stopPropagation(); onShowMemory(); }}>
            {getUILoc('YONOTSUNE.HUD.MEMORY') || '記憶'}
          </button>
          <button className="yonotsune-btn check-btn" style={{ padding: '4px 10px', fontSize: '0.75rem', marginRight: '5px' }} onClick={(e) => { e.stopPropagation(); onCheckDeck(); }}>
            {getUILoc('LABELS.DECK')}
          </button>
          <button className="yonotsune-btn suspend-btn" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={(e) => { e.stopPropagation(); onSuspend(); }}>
            {getUILoc('YONOTSUNE.SUSPEND_RUN') || '一時中断'}
          </button>
        </div>
        {run.matchedCount >= 5 && (
          <div className="hud-item boss-hud">
            <span className="label text-danger">{getUILoc('YONOTSUNE.HUD.FINAL_GOAL')}:</span>
            <span className="value text-danger">{localizedBossName}</span>
          </div>
        )}
        <button className="yonotsune-btn reset-btn" onClick={onAbandon}>
          {getUILoc('YONOTSUNE.ABANDON_RUN')}
        </button>
      </div>
    );
  };

  const renderDeckPreviewModal = () => {
    if (!previewingDeck || !activeRun) return null;

    const { playerDeck } = activeRun;
    const grouped = groupCards(playerDeck);
    const totalCount = playerDeck.length;

    return (
      <div className="player-details-overlay pile-detail-overlay" style={{ zIndex: 3000 }} onClick={() => setPreviewingDeck(false)}>
        <div className="player-details-modal pile-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="player-details-header">
            <h3>
              {getUILoc('LABELS.DECK')} ({getUILoc('UNITS.CARDS', { count: totalCount })})
            </h3>
            <button className="close-button" onClick={() => setPreviewingDeck(false)}>×</button>
          </div>
          <div className="player-details-content">
            <div className="pile-cards-grid">
              {grouped.map((item) => {
                const probability = totalCount > 0 ? (item.count / totalCount) * 100 : 0;
                const cardDef = cardDefs?.[item.id];
                return (
                  <div 
                    key={item.id} 
                    className="pile-card-group"
                    onClick={() => setSelectedDetailCard(cardDef || { id: item.id })}
                  >
                    <div className="pile-card-illustration-wrapper">
                      <CardIllustration cardId={item.id} isMini={true} />
                      <div className="pile-card-count-badge">×{item.count}</div>
                      <div className="pile-card-prob-badge">{probability.toFixed(2)}%</div>
                    </div>
                    <div className="pile-card-name">{getCardName(item.id)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMemoryPreviewModal = () => {
    if (!previewingMemory || !activeRun) return null;

    const deckConfig = INITIAL_DECKS[activeRun.initialDeckKey];
    if (!deckConfig) return null;

    const effect = deckConfig.memoryEffect;

    return (
      <div className="player-details-overlay pile-detail-overlay" style={{ zIndex: 99999 }} onClick={() => setPreviewingMemory(false)}>
        <div className="player-details-modal pile-detail-modal memory-preview-modal" onClick={(e) => e.stopPropagation()}>
          <div className="player-details-header">
            <h3>{getUILoc(deckConfig.nameKey)}</h3>
            <button className="close-button" onClick={() => setPreviewingMemory(false)}>×</button>
          </div>
          <div className="player-details-content">
            <p>{getUILoc(deckConfig.descriptionKey)}</p>
            <hr />
            <h4>{getUILoc('YONOTSUNE.LABELS.EFFECT') || '効果'}</h4>
            <p>{getUILoc(effect.key)}</p>
          </div>
        </div>
      </div>
    );
  };

  // ----------------------------------------------------
  // ボス開示オーバーレイ (モーダル)
  // ----------------------------------------------------
  const BossRevealModal = ({ run }) => {
    if (!run?.showBossReveal) return null;
    const boss = BOSS_DECKS[run.bossKey];
    const localizedBossName = boss ? getUILoc(boss.nameKey) : (run.bossDeckName || run.bossKey || '???');
    
    return (
      <div className="yonotsune-modal-overlay boss-reveal-modal">
        <div className="resolver-body reveal-box modal-content">
          <h1 className="yonotsune-title">{getUILoc('YONOTSUNE.REWARD_SCREEN.BOSS_REVEAL_TITLE')}</h1>
          <p className="resolver-intro">{getUILoc('YONOTSUNE.REWARD_SCREEN.BOSS_REVEAL_DESC')}</p>
          <div className="boss-card">
            <h2 className="glow-text">{localizedBossName}</h2>
            <div className="boss-stats">
              <span className="text-heal">{getUILoc('YONOTSUNE.HUD.INIT_CON')}: {12 + (boss?.consciousness || 15)}</span>
              <span className="text-scale">{getUILoc('YONOTSUNE.HUD.INIT_SCALE')}: {5 + (boss?.scale || 10)}</span>
            </div>
          </div>
          <button className="yonotsune-btn start-btn" onClick={handleCloseBossReveal}>
            {getUILoc('YONOTSUNE.REWARD_SCREEN.CONTINUE')}
          </button>
        </div>
      </div>
    );
  };

  // ----------------------------------------------------
  // 画面レンダリング分岐 (currentView に基づく出し分け)
  // ----------------------------------------------------
  if (currentView === 'menu') {
    return (
      <>
        <YonotsuneMenu
          onStart={activeRun ? () => { playSE('select'); setCurrentView('run'); } : handleStartNewRun}
          onShowAchievements={handleShowAchievements}
          onShowRecords={() => { playSE('select'); setShowRecords(true); }}
          onShowHelp={() => { playSE('select'); setShowHelp(true); }}
          onBack={onBackToTitle}
          toast={toast}
          hasActiveRun={!!activeRun}
          matchedCount={activeRun ? activeRun.matchedCount : 0}
        />
        <YonotsuneHelpOverlay isOpen={showHelp} onClose={() => { playSE('cancel'); setShowHelp(false); }} />
        {showRecords && <YonotsuneRecordsModal records={records} onClose={() => { playSE('cancel'); setShowRecords(false); }} />}
      </>
    );
  }

  if (currentView === 'achievements') {
    return (
      <YonotsuneAchievementScreen
        achievements={achievements}
        onExport={exportAchievements}
        onImport={importAchievements}
        onUnlockSecret={yonotsune.unlockAchievement}
        onReset={yonotsune.resetAchievements}
        onClose={handleCloseSubView}
      />
    );
  }

  if (currentView === 'deck_selection') {
    return (
      <YonotsuneInitialDeckSelection
        cardDefs={cardDefs}
        onSelect={handleSelectDeck}
        onBack={handleCloseSubView}
        records={records}
      />
    );
  }

  // ----------------------------------------------------
  // 進行中画面 (currentView === 'run' の場合)
  // ----------------------------------------------------
  if (!activeRun) {
    return (
      <>
        <YonotsuneMenu
          onStart={handleStartNewRun}
          onShowAchievements={handleShowAchievements}
          onShowRecords={() => { playSE('select'); setShowRecords(true); }}
          onBack={onBackToTitle}
          toast={toast}
          hasActiveRun={false}
          matchedCount={0}
        />
        {showRecords && <YonotsuneRecordsModal records={records} onClose={() => { playSE('cancel'); setShowRecords(false); }} />}
      </>
    );
  }

  // ----------------------------------------------------
  // 進行中画面
  // ----------------------------------------------------
  const handleAbandonClick = () => {
    playSE('select');
    setShowAbandonConfirm(true);
  };

  const handleAbandonConfirm = () => {
    playSE('cancel');
    resetRun();
    setCurrentView('menu');
    setShowAbandonConfirm(false);
  };

  const handleAbandonCancel = () => {
    playSE('cancel');
    setShowAbandonConfirm(false);
  };

  // 中断復帰時などの判定：対局結果が保留されている場合は報酬画面を優先して出す
  if (activeRun.pendingMatchResult) {
    return (
      <div className="yonotsune-screen run-active">
        <div className="bg-glitch-overlay"></div>
        <YonotsuneHUD 
          run={activeRun} 
          onAbandon={handleAbandonClick} 
          onCheckDeck={handleOpenDeckPreview}
          onShowMemory={handleOpenMemoryPreview} 
          onSuspend={() => { playSE('cancel'); setCurrentView('menu'); }}
        />
        
        {/* モーダルとして表示（GameOverScreenと同一のスタイルを適用） */}
        <div className="yonotsune-reward-overlay">
          <div className="yonotsune-reward-content">
            <YonotsuneRewardScreen
              yonotsune={yonotsune}
              gameState={null} // gameStateがない状態（中断復帰）でも保存データから復元される
              onReturnToMenu={() => setCurrentView('menu')}
              onScreenChange={(view) => {
                // viewが'yonotsune'なら報酬適用後のノード選択へ戻る
                if (view === 'yonotsune') {
                  setCurrentView('menu');
                }
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="yonotsune-screen run-active">
      <div className="bg-glitch-overlay"></div>
      <YonotsuneHUD 
        run={activeRun} 
        onAbandon={handleAbandonClick} 
        onCheckDeck={handleOpenDeckPreview}
        onShowMemory={handleOpenMemoryPreview} 
        onSuspend={() => { playSE('cancel'); setCurrentView('menu'); }}
      />
      
      {showAbandonConfirm && (
        <div className="choice-prompt-overlay">
          <div className="choice-prompt">
            <h3>{getUILoc('YONOTSUNE.CONFIRM_ABANDON')}</h3>
            <p>{getUILoc('YONOTSUNE.SURRENDER_WARNING') || getUILoc('YONOTSUNE.SURRENDER_CONFIRM')}</p>
            <div className="choice-options">
              <button className="yonotsune-btn" onClick={handleAbandonConfirm}>{getUILoc('BUTTONS.YES')}</button>
              <button className="yonotsune-btn" onClick={handleAbandonCancel}>{getUILoc('BUTTONS.NO')}</button>
            </div>
          </div>
        </div>
      )}
      
      {activeRun.activeNode === null ? (
        <YonotsuneNodeMap
          activeRun={activeRun}
          cardDefs={cardDefs}
          onSelectNode={(nodeId) => { playSE('select'); selectNode(nodeId); }}
          onAbandonRun={handleAbandonClick}
          hideHUD={true}
        />
      ) : (
        <YonotsuneEventResolver
          activeRun={activeRun}
          cardDefs={cardDefs}
          onStartMatch={() => { playSE('ideology_place'); onStartMatch(); }}
          executeWhimExchange={(card) => { playSE('ideology_place'); return executeWhimExchange(card); }}
          executeSharpenRemove={(card) => { playSE('damage'); return executeSharpenRemove(card); }}
          executeDiplomacyExchange={(target, cards) => { playSE('select'); return executeDiplomacyExchange(target, cards); }}
          requestDiplomacyReroll={() => { playSE('select'); return requestDiplomacyReroll(); }}
          executeOpportunityRemove={(cards) => { playSE('damage'); return executeOpportunityRemove(cards); }}
          executeOpportunityDraw={() => { playSE('select'); return executeOpportunityDraw(); }}
          startOpportunityBattle={() => { playSE('select'); return startOpportunityBattle(); }}
          goToNextNodeSelection={() => { playSE('select'); goToNextNodeSelection(); }}
          executeCrystal={() => { playSE('ideology_place'); executeCrystal(); }}
          hesitateCrystal={() => { playSE('ideology_place'); hesitateCrystal(); }}
        />
      )}

      {/* デッキ確認モーダル */}
      {renderDeckPreviewModal()}
      
      {/* 記憶確認モーダル */}
      {renderMemoryPreviewModal()}

      {/* カード詳細モーダル */}
      {selectedDetailCard && (
        <CardDetail 
          card={selectedDetailCard} 
          cardDefs={cardDefs}
          onClose={() => setSelectedDetailCard(null)} 
        />
      )}

      {/* ボス開示モーダル */}
      <BossRevealModal run={activeRun} />
    </div>
  );
};
export default YonotsuneScreen;
